import React, { SyntheticEvent, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../Components/Redux/store';
import { setAuth } from '../Components/Redux/authSlice';
import Sidebar from '../Components/Sidebar';
import 'bootstrap/dist/css/bootstrap.min.css';

interface Branch {
  id: number;
  name: string;
}

const Register: React.FC = () => {
  const [first_name, setFirstName] = useState('');
  const [last_name, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [userType, setUserType] = useState('admin');
  const [branch, setBranch] = useState<number | null>(null);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [formInvalid, setFormInvalid] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const dispatch = useDispatch();
  const [userRole, setUserRole] = useState<string>('');
  const auth = useSelector((state: RootState) => state.auth.value);
  const navigate = useNavigate();

  const getCookie = (name: string) => {
    const cookieName = name + "=";
    const decodedCookie = decodeURIComponent(document.cookie);
    const cookieArray = decodedCookie.split(';');
    for (let i = 0; i < cookieArray.length; i++) {
      let cookie = cookieArray[i];
      while (cookie.charAt(0) === ' ') {
        cookie = cookie.substring(1);
      }
      if (cookie.indexOf(cookieName) === 0) {
        return cookie.substring(cookieName.length, cookie.length);
      }
    }
    return '';
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = getCookie('token');
        if (!token) {
          dispatch(setAuth(false));
          setLoading(false);
          return;
        }
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        const { data } = await axios.get('/users/user');
        setUserRole(data.user.role);
        // Auto-set the current user's branch
        if (data.user.branch) {
          setBranch(data.user.branch);
        }
        dispatch(setAuth(true));
      } catch (e) {
        dispatch(setAuth(false));
      } finally {
        setLoading(false);
      }
    };

    const fetchBranches = async () => {
      try {
        const response = await axios.get('/Branches/branches/');
        setBranches(response.data);
      } catch (err) {
        console.error('Error fetching branches:', err);
        setError('Error fetching branches');
      }
    };

    fetchData();
    fetchBranches();
  }, [dispatch]);

  const submit = async (e: SyntheticEvent) => {
    e.preventDefault();

    if (!first_name || !last_name || !email || !password || !passwordConfirm || !branch || !userType) {
      setFormInvalid(true);
      setError('Fadlan buuxi dhamaan meelaha loo baahan yahay');
      return;
    }

    if (password !== passwordConfirm) {
      setError('Lambarka sirta iyo xaqiijinta isma laha');
      return;
    }

    try {
      const selectedBranch = branches.find(b => b.id === branch);
      await axios.post('/users/register', {
        first_name,
        last_name,
        email,
        password,
        password_confirm: passwordConfirm,
        role: userType,
        branch,
        branch_name: selectedBranch ? selectedBranch.name : ''
      });
      setSuccessMessage('Isticmaalaha si guul leh ayaa loo diiwaangeliyay');
      setError(null);
      setFormInvalid(false);
      setFirstName('');
      setLastName('');
      setEmail('');
      setPassword('');
      setPasswordConfirm('');
      setUserType('admin');
      setBranch(null);
      setTimeout(() => {
        navigate('/users');
      }, 2000);
    } catch (error: any) {
      console.error('Error registering user:', error);
      setError(error.response?.data?.detail || 'Khalad ayaa dhacay markii la diiwaangelinayay isticmaalaha');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen w-full">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!auth) {
    return (
      <div className="container-fluid min-h-screen bg-gray-100 flex justify-center items-center">
        <div className="text-center">
          <h4 className="text-gray-600 text-lg md:text-xl mb-3">Ma haysatid ogolaansho (Not Authenticated)</h4>
          <a href="/login" className="btn btn-primary bg-blue-600 hover:bg-blue-700 transition-colors duration-200">
            Aad Bogga Gelitaanka
          </a>
        </div>
      </div>
    );
  }

  if (userRole === 'accountant') {
    return (
      <div className="container-fluid min-h-screen bg-gray-100 flex justify-center items-center">
        <div className="text-center">
          <h4 className="text-gray-600 text-lg md:text-xl mb-3">Ma haysatid ogolaansho aad ku aragto boggan</h4>
          <a href="/dashboard" className="btn btn-primary bg-blue-600 hover:bg-blue-700 transition-colors duration-200">
            Ku Noqo Muuraayada
          </a>
        </div>
      </div>
    );
  }

  return (
    <>
      <Sidebar />
      <div className="container-fluid p-4 min-h-screen bg-gray-100 w-full lg:ml-[250px] lg:w-[calc(100%-250px)]">
        <div className="row justify-content-center">
          <div className="col-12 col-md-6 col-lg-5">
            <div className="card shadow-lg border-0 rounded-lg">
              <div className="card-body p-4 p-md-5">
                <h1 className="text-center mb-4 text-xl md:text-2xl font-bold text-gray-800">Diiwaangeli Isticmaale Cusub</h1>
                {successMessage && (
                  <div className="alert alert-success alert-dismissible fade show" role="alert">
                    {successMessage}
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setSuccessMessage(null)}
                      aria-label="Close"
                    ></button>
                  </div>
                )}
                {error && (
                  <div className="alert alert-danger alert-dismissible fade show" role="alert">
                    {error}
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setError(null)}
                      aria-label="Close"
                    ></button>
                  </div>
                )}
                <form onSubmit={submit}>
                  <div className="mb-3">
                    <label htmlFor="userType" className="form-label font-semibold text-gray-700">Nooca Isticmaalaha</label>
                    <select
                      className="form-select border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                      id="userType"
                      value={userType}
                      onChange={(e) => setUserType(e.target.value)}
                      required
                    >
                      <option value="admin">Maamule (Admin)</option>
                      <option value="teacher">Macalin</option>
                      <option value="accountant">Xisaabiye</option>
                      <option value="parent">Waalid</option>
                    </select>
                  </div>
                  <div className="mb-3">
                    <label htmlFor="firstName" className="form-label font-semibold text-gray-700">Magaca Koowaad</label>
                    <input
                      type="text"
                      className={`form-control ${formInvalid && !first_name ? 'is-invalid' : ''}`}
                      id="firstName"
                      placeholder="Magaca Koowaad"
                      value={first_name}
                      onChange={e => setFirstName(e.target.value)}
                      required
                    />
                    {formInvalid && !first_name && <div className="invalid-feedback">Magaca koowaad waa loo baahan yahay</div>}
                  </div>
                  <div className="mb-3">
                    <label htmlFor="lastName" className="form-label font-semibold text-gray-700">Magaca Labaad</label>
                    <input
                      type="text"
                      className={`form-control ${formInvalid && !last_name ? 'is-invalid' : ''}`}
                      id="lastName"
                      placeholder="Magaca Labaad"
                      value={last_name}
                      onChange={e => setLastName(e.target.value)}
                      required
                    />
                    {formInvalid && !last_name && <div className="invalid-feedback">Magaca labaad waa loo baahan yahay</div>}
                  </div>
                  <div className="mb-3">
                    <label htmlFor="email" className="form-label font-semibold text-gray-700">Ciwaanka Email-ka</label>
                    <input
                      type="email"
                      className={`form-control ${formInvalid && !email ? 'is-invalid' : ''}`}
                      id="email"
                      placeholder="name@example.com"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      required
                    />
                    {formInvalid && !email && <div className="invalid-feedback">Email waa loo baahan yahay</div>}
                  </div>
                  <div className="mb-3">
                    <label htmlFor="password" className="form-label font-semibold text-gray-700">Lambarka Sirta</label>
                    <input
                      type="password"
                      className={`form-control ${formInvalid && !password ? 'is-invalid' : ''}`}
                      id="password"
                      placeholder="Lambarka Sirta"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      required
                    />
                    {formInvalid && !password && <div className="invalid-feedback">Lambarka sirta waa loo baahan yahay</div>}
                  </div>
                  <div className="mb-3">
                    <label htmlFor="passwordConfirm" className="form-label font-semibold text-gray-700">Xaqiiji Lambarka Sirta</label>
                    <input
                      type="password"
                      className={`form-control ${formInvalid && !passwordConfirm ? 'is-invalid' : ''}`}
                      id="passwordConfirm"
                      placeholder="Xaqiiji Lambarka Sirta"
                      value={passwordConfirm}
                      onChange={e => setPasswordConfirm(e.target.value)}
                      required
                    />
                    {formInvalid && !passwordConfirm && <div className="invalid-feedback">Xaqiijinta lambarka sirta waa loo baahan yahay</div>}
                  </div>
                  <div className="mb-3">
                    <label htmlFor="branch" className="form-label font-semibold text-gray-700">Laanta (Si toos ah ayaa looga doortay ciwaankaaga)</label>
                    <select
                      className="form-select bg-gray-100"
                      id="branch"
                      value={branch || ''}
                      onChange={(e) => setBranch(parseInt(e.target.value) || null)}
                      disabled
                      required
                    >
                      <option value="">Dooro Laan</option>
                      {branches.map((b) => (
                        <option key={b.id} value={b.id}>{b.name}</option>
                      ))}
                    </select>
                    <small className="text-muted">Isticmaalayaasha cusub waxaa lagu diiwaangelin doonaa laantaada haatan</small>
                  </div>
                  <div className="d-grid">
                    <button
                      type="submit"
                      className="btn btn-primary btn-lg bg-blue-600 hover:bg-blue-700 transition-colors duration-200"
                    >
                      Diiwaangeli
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Register;